import { z } from 'zod';
import { PointSchema } from './geojson';

/** Asset-class taxonomy (mirrors the DB CHECK on response_assets.asset_class). */
export const AssetClassSchema = z.enum([
  'fire_station',
  'wildfire_base',
  'aviation_base',
  'marine_vessel',
  'medical_facility',
  'volunteer_chapter',
  'utility_response',
  'specialty_team',
  'umbrella_org',
  'illustrative_marker',
  // 'aviation_aircraft' exists in the DB CHECK for future per-airframe rows;
  // R.1 keeps live aircraft in tracked_aircraft, so it's not emitted here yet.
  'aviation_aircraft',
]);
export type AssetClass = z.infer<typeof AssetClassSchema>;

export const ResponseAssetFeaturePropertiesSchema = z.object({
  name: z.string(),
  source: z.string().nullable(),
  external_id: z.string().nullable(),
  asset_class: z.string().nullable(),
  operator_org: z.string().nullable(),
  status: z.string(),
  illustrative: z.boolean(),
  /** Ontario Corps capability sub-category (V.3). NULL for non-OC rows. */
  oc_category: z.string().nullable(),
  parent_org_id: z.string().uuid().nullable(),
  response_role: z.string().nullable(),
  service_radius_km: z.number().nullable(),
  call_sign: z.string().nullable(),
  tail_number: z.string().nullable(),
  icao_hex: z.string().nullable(),
  aircraft_type: z.string().nullable(),
  engine_count: z.number().int().nullable(),
  vessel_class: z.string().nullable(),
  vessel_count: z.number().int().nullable(),
  primary_waters: z.string().nullable(),
  // R.4.1 Resource Status Lifecycle — the operator-facing readiness picture. The
  // API emits these on every list + detail row (response-assets.ts assetProperties);
  // defaults keep older callers tolerant if a field is ever absent.
  lifecycle_state: z.string().default('available'),
  readiness: z.string().default('unknown'),
  time_to_deploy_band: z.string().nullable().default(null),
  capability_tags: z.array(z.string()).default([]),
});
export type ResponseAssetFeatureProperties = z.infer<
  typeof ResponseAssetFeaturePropertiesSchema
>;

export const ResponseAssetFeatureSchema = z.object({
  type: z.literal('Feature'),
  id: z.string().uuid(),
  geometry: PointSchema,
  properties: ResponseAssetFeaturePropertiesSchema,
});
export type ResponseAssetFeature = z.infer<typeof ResponseAssetFeatureSchema>;

export const ResponseAssetFeatureCollectionSchema = z.object({
  type: z.literal('FeatureCollection'),
  features: z.array(ResponseAssetFeatureSchema),
});
export type ResponseAssetFeatureCollection = z.infer<
  typeof ResponseAssetFeatureCollectionSchema
>;

/** A tracked aircraft as surfaced on an operator asset's detail payload. */
export const TrackedAircraftSummarySchema = z.object({
  id: z.string().uuid(),
  tail_number: z.string(),
  icao_hex: z.string().nullable(),
  aircraft_type: z.string(),
  aircraft_role: z.string().nullable(),
  engine_count: z.number().int().nullable(),
  operator_org: z.string().nullable(),
  base_name: z.string().nullable(),
  status: z.string(),
});
export type TrackedAircraftSummary = z.infer<typeof TrackedAircraftSummarySchema>;

/** A child asset (partner / illustrative marker) on a parent's detail payload. */
export const ChildAssetSummarySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  asset_class: z.string().nullable(),
  illustrative: z.boolean(),
});
export type ChildAssetSummary = z.infer<typeof ChildAssetSummarySchema>;

export const ResponseAssetDetailSchema = z.object({
  type: z.literal('Feature'),
  id: z.string().uuid(),
  geometry: PointSchema.nullable(),
  properties: ResponseAssetFeaturePropertiesSchema.extend({
    metadata: z.record(z.unknown()).nullable(),
    children: z.array(ChildAssetSummarySchema),
    aircraft: z.array(TrackedAircraftSummarySchema),
  }),
});
export type ResponseAssetDetail = z.infer<typeof ResponseAssetDetailSchema>;
